/**
 * Footer priority-page rotation (master plan §0 item 136).
 *
 * A small curated slot in the site footer that rotates through priority pages
 * needing internal-link authority. The slot renders sitewide (it lives in
 * components/site-footer.tsx), so every page it surfaces gains a fresh sitewide
 * internal link that helps it accelerate.
 *
 * Design guarantees:
 *  - CONFIG-DRIVEN: the whole slot is described by the constants in this file.
 *    Add/remove a priority page by editing FOOTER_ROTATION_POOL only.
 *  - REVERSIBLE: set FOOTER_ROTATION_ENABLED = false to remove the slot
 *    entirely (the footer renders exactly as it did before), or empty the pool.
 *    "Swap a link out once its page is stable" = delete that pool entry.
 *  - NON-INDEX-CHANGING: this only adds internal links between already-live,
 *    already-indexable pages. It touches no canonical, robots, or sitemap. It
 *    never introduces a new URL — every href must be an existing public route.
 *  - DETERMINISTIC & WEEK-STABLE: the window is seeded by the UTC week (whole
 *    7-day periods since the Unix epoch), so the rendered links are IDENTICAL
 *    for every request within a UTC week — crawlers see a consistent footer —
 *    and the window advances by one pool entry at the week boundary. No
 *    per-request randomness anywhere. (Server and client agree except across
 *    the sub-second week-boundary tick — the same risk profile as the footer's
 *    existing `new Date().getFullYear()` copyright line.)
 *  - NON-REDUNDANT: never add a page that is already permanently linked from
 *    the footer or header chrome (e.g. /data, /rebates, /compare, /local,
 *    /press, /sitemap). A rotation slot for an already-sitewide-linked page
 *    adds zero incremental authority — the slot exists for pages that have NO
 *    other sitewide link.
 *  - GATED: collection hrefs pass through the same render-time indexed gate as
 *    every other footer column (GatedFooterLinkList → keepIndexedCollectionLinks),
 *    so a collection that loses its indexed disposition silently drops out.
 *    Known-empty collections are filtered here as well.
 *  - INTERNAL-SAFE: any /admin-panel, /operations, /_qa, /mockups, /docs, /r,
 *    /api, or /health href is dropped defensively, so the rotation can NEVER
 *    leak an internal/admin route into the public footer (owner ruling
 *    07/17/2026, plan item 175 — no internal links from the public site, ever).
 */

import { collectionSlugFromHref } from "@/lib/chrome-link-gate"
import { isEmptyCollection } from "@/lib/empty-collections"

export type FooterRotationLink = { title: string; href: string }

/** Master on/off switch — false renders the footer with no rotation slot. */
export const FOOTER_ROTATION_ENABLED = true

/** How many pool entries appear in the slot at once. */
export const FOOTER_ROTATION_WINDOW = 4

/**
 * Curated priority pages that currently benefit from extra sitewide internal
 * links. Order is not meaningful — the rotation walks the whole pool over
 * time. Keep every entry a real, live, indexable public route that is NOT
 * already linked from the permanent footer/header chrome. Remove a page once
 * it is stable and no longer needs the boost.
 *
 * Current pool (verified live + robots-indexable + sitemap-listed 07/19/2026):
 *  - /financing, /warranty        — W22/W23 authority hubs, no other sitewide link
 *  - /data/price-index/archive    — monthly issue archive, only linked from /data
 *  - /deals                       — live price-drop hub
 *  - /black-friday-refrigerator-deals — seasonal hub accruing authority pre-Q4
 *  - /recalls                     — safety hub with its own sitemap
 *  - three keg/beverage niche-expansion head collections (indexed per
 *    docs/Page-Types-And-Templates.md) that have no footer link
 */
export const FOOTER_ROTATION_POOL: readonly FooterRotationLink[] = [
  { title: "Refrigerator Financing Guide", href: "/financing" },
  { title: "Refrigerator Warranty Guide", href: "/warranty" },
  { title: "Price Index Archive", href: "/data/price-index/archive" },
  { title: "Refrigerator Deals", href: "/deals" },
  { title: "Black Friday Refrigerator Deals", href: "/black-friday-refrigerator-deals" },
  { title: "Refrigerator Recalls", href: "/recalls" },
  { title: "Beverage Coolers", href: "/collections/beverage-coolers" },
  { title: "Commercial Kegerators", href: "/collections/commercial-kegerators" },
]

/**
 * Route prefixes that must never appear in the public footer. Belt-and-braces
 * enforcement of plan item 175 — even if a future edit adds one of these to the
 * pool by mistake, it is filtered out before render.
 */
const INTERNAL_PREFIXES = [
  "/admin-panel",
  "/operations",
  "/_qa",
  "/qa",
  "/mockups",
  "/docs",
  "/health",
  "/api",
  "/r/",
] as const

function isPublicHref(href: string): boolean {
  if (!href.startsWith("/")) return false // external / non-path hrefs never rotate here
  return !INTERNAL_PREFIXES.some(
    (prefix) => href === prefix || href.startsWith(prefix + "/") || href.startsWith(prefix),
  )
}

/** Drop known-empty collections (mirrors the other footer columns). */
function isLinkableCollection(href: string): boolean {
  const slug = collectionSlugFromHref(href)
  return slug === null || !isEmptyCollection(slug)
}

/** Whole 7-day periods since the Unix epoch in UTC — the rotation seed. */
function utcWeekIndex(now: Date): number {
  const epochDay = Math.floor(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 86_400_000,
  )
  return Math.floor(epochDay / 7)
}

/**
 * The links to render in the footer rotation slot for a given instant.
 * Returns a stable window of the pool — identical for every instant within a
 * UTC week — that advances by one entry at each UTC-week boundary and wraps
 * around, so every priority page cycles through the slot over time.
 * Returns [] when the feature is disabled or the (public-filtered) pool is empty.
 */
export function getFooterRotationLinks(now: Date = new Date()): FooterRotationLink[] {
  if (!FOOTER_ROTATION_ENABLED) return []
  const pool = FOOTER_ROTATION_POOL.filter(
    (link) => isPublicHref(link.href) && isLinkableCollection(link.href),
  )
  if (pool.length === 0) return []

  const window = Math.min(FOOTER_ROTATION_WINDOW, pool.length)
  const start = ((utcWeekIndex(now) % pool.length) + pool.length) % pool.length

  const out: FooterRotationLink[] = []
  for (let i = 0; i < window; i++) {
    out.push(pool[(start + i) % pool.length])
  }
  return out
}
